import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import Button from '../Button';
import styles from './styles.module.css';

const ExcelIcon = () => (
  <svg width="40" height="40" viewBox="0 0 309.267 309.267" fill="#000000" className={styles.optionIcon} xmlns="http://www.w3.org/2000/svg">
    <g strokeWidth="0">
      <path fill="#3DB39E" d="M38.658,0h164.23l87.049,86.711v203.227c0,10.679-8.659,19.329-19.329,19.329H38.658 c-10.67,0-19.329-8.65-19.329-19.329V19.329C19.329,8.65,27.989,0,38.658,0z" />
      <path fill="#2F8A78" d="M289.658,86.981h-67.372c-10.67,0-19.329-8.659-19.329-19.329V0.193L289.658,86.981z" />
      <path fill="#8BD1C5" d="M77.317,125.64v125.64H231.95V125.64H77.317z M193.292,135.304v19.329h-19.329v-19.329H193.292z M135.304,183.627h-19.329v-19.32h19.329V183.627z M144.969,164.308h19.329v19.32h-19.329V164.308z M135.304,193.302v19.32h-19.329 v-19.32H135.304z M144.969,193.302h19.329v19.32h-19.329V193.302z M173.963,193.302h19.329v19.32h-19.329V193.302z M173.963,183.627v-19.32h19.329v19.32L173.963,183.627z M164.298,135.304v19.329h-19.329v-19.329H164.298z M135.304,135.304v19.329h-19.329v-19.329H135.304z M86.981,135.304h19.329v19.329H86.981V135.304z M86.981,164.308h19.329v19.32 H86.981V164.308z M86.981,193.302h19.329v19.32H86.981V193.302z M86.981,241.625v-19.339h19.329v19.339H86.981z M115.975,241.625 v-19.339h19.329v19.339H115.975z M144.969,241.625v-19.339h19.329v19.339H144.969z M173.963,241.625v-19.339h19.329v19.339H173.963 z M222.286,241.625h-19.329v-19.339h19.329V241.625z M222.286,212.621h-19.329v-19.32h19.329V212.621z M222.286,183.627h-19.329 v-19.32h19.329V183.627z M222.286,154.634h-19.329v-19.329h19.329V154.634z" />
    </g>
  </svg>
);

const PdfIcon = () => (
  <svg width="40" height="40" viewBox="0 0 309.267 309.267" fill="#000000" className={styles.optionIcon} xmlns="http://www.w3.org/2000/svg">
    <g strokeWidth="0">
      <path fill="#E2574C" d="M38.658,0h164.23l87.049,86.711v203.227c0,10.679-8.659,19.329-19.329,19.329H38.658 c-10.67,0-19.329-8.65-19.329-19.329V19.329C19.329,8.65,27.989,0,38.658,0z" />
      <path fill="#B53629" d="M289.658,86.981h-67.372c-10.67,0-19.329-8.659-19.329-19.329V0.193L289.658,86.981z" />
      <path fill="#FFFFFF" d="M217.434,146.544c3.238,0,4.823-2.822,4.823-5.557c0-2.832-1.653-5.567-4.823-5.567h-18.44 c-3.605,0-5.615,2.986-5.615,6.282v45.317c0,4.04,2.3,6.282,5.412,6.282c3.093,0,5.403-2.242,5.403-6.282v-12.438h11.153 c3.46,0,5.19-2.832,5.19-5.644c0-2.754-1.73-5.49-5.19-5.49h-11.153v-16.903C204.194,146.544,217.434,146.544,217.434,146.544z M155.107,135.42h-13.492c-3.663,0-6.263,2.513-6.263,6.243v45.395c0,4.629,3.74,6.079,6.417,6.079h14.159 c16.758,0,27.824-11.027,27.824-28.047C183.743,147.095,173.325,135.42,155.107,135.42z M155.755,181.946h-8.225v-35.334h7.413 c11.221,0,16.101,7.529,16.101,17.918C171.044,174.253,166.25,181.946,155.755,181.946z M106.33,135.42H92.964 c-3.779,0-5.886,2.493-5.886,6.282v45.317c0,4.04,2.416,6.282,5.663,6.282s5.663-2.242,5.663-6.282v-13.231h8.379 c10.341,0,18.875-7.326,18.875-19.107C125.659,143.152,117.425,135.42,106.33,135.42z M106.108,163.158h-7.703v-17.097h7.703 c4.755,0,7.78,3.711,7.78,8.553C113.878,159.447,110.863,163.158,106.108,163.158z" />
    </g>
  </svg>
);

const ICON_MAP = { excel: ExcelIcon, pdf: PdfIcon };

const EXPORT_OPTIONS = [
  {
    value: 'excel',
    label: 'Excel Spreadsheet (.xlsx)',
    description: 'Best for data analysis, calculations, and detailed inventory tracking.',
    icon: 'excel',
  },
  {
    value: 'pdf',
    label: 'PDF Document (.pdf)',
    description: 'Ideal for sharing reports, printing, or archival purposes.',
    icon: 'pdf',
  },
];

const EMPTY_REPORT_TYPES = [];

const ExportFormatDialog = ({
  open,
  onClose,
  onConfirm,
  title = 'Export Inventory Data',
  subtitle = 'Select your preferred file format for the export report.',
  options = EXPORT_OPTIONS,
  reportTypes = EMPTY_REPORT_TYPES,
  confirmLabel = 'Export Data',
  closeLabel = 'Cancel',
}) => {
  const hasReportTypeSelection = reportTypes.length > 0;
  const [selectedReportType, setSelectedReportType] = useState(reportTypes[0]?.value ?? '');
  const [selectedFormat, setSelectedFormat] = useState(options[0]?.value ?? 'excel');
  const wasOpenRef = useRef(false);

  const getAllowedOptions = useCallback(
    (reportType) => {
      if (!hasReportTypeSelection) return options;
      return options.filter((option) => {
        if (!Array.isArray(option.reportTypes) || option.reportTypes.length === 0) {
          return true;
        }
        return option.reportTypes.includes(reportType);
      });
    },
    [hasReportTypeSelection, options]
  );

  // Reset selection only when the dialog opens — not on every parent re-render
  // (inline options/reportTypes arrays get new references each render).
  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!justOpened) return;

    if (hasReportTypeSelection) {
      const firstReportType = reportTypes[0]?.value ?? '';
      setSelectedReportType(firstReportType);
      const allowedOptions = getAllowedOptions(firstReportType);
      setSelectedFormat(allowedOptions[0]?.value ?? '');
      return;
    }
    if (options.length > 0) {
      setSelectedFormat(options[0].value);
    }
  }, [open, hasReportTypeSelection, reportTypes, options, getAllowedOptions]);

  useEffect(() => {
    if (!open || !hasReportTypeSelection || !selectedReportType) return;
    const allowedOptions = getAllowedOptions(selectedReportType);
    const isCurrentFormatAllowed = allowedOptions.some(
      (option) => option.value === selectedFormat
    );
    if (!isCurrentFormatAllowed) {
      setSelectedFormat(allowedOptions[0]?.value ?? '');
    }
  }, [open, hasReportTypeSelection, selectedReportType, selectedFormat, getAllowedOptions]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const handleConfirm = useCallback(() => {
    if (hasReportTypeSelection) {
      onConfirm?.({
        reportType: selectedReportType,
        format: selectedFormat,
      });
    } else {
      onConfirm?.(selectedFormat);
    }
    onClose?.();
  }, [onConfirm, onClose, selectedFormat, hasReportTypeSelection, selectedReportType]);

  if (!open) return null;

  const formatOptions = hasReportTypeSelection
    ? getAllowedOptions(selectedReportType)
    : options;

  const confirmDisabled = !selectedFormat || (hasReportTypeSelection && !selectedReportType);

  return ReactDOM.createPortal(
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="export-dialog-title"
      onClick={onClose}
    >
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <h2 id="export-dialog-title" className={styles.modalTitle}>
            {title}
          </h2>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
        <div className={styles.modalBody}>
          {hasReportTypeSelection && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>1. Select report type</h3>
              <div className={styles.options} role="radiogroup" aria-label="Report type">
                {reportTypes.map((reportType) => {
                  const isSelected = selectedReportType === reportType.value;
                  return (
                    <label
                      key={reportType.value}
                      className={`${styles.optionCard} ${isSelected ? styles.optionCardSelected : ''}`}
                      onClick={() => setSelectedReportType(reportType.value)}
                    >
                      <input
                        type="radio"
                        name="report-type"
                        value={reportType.value}
                        checked={isSelected}
                        onChange={() => setSelectedReportType(reportType.value)}
                        className={styles.radio}
                      />
                      <div className={styles.optionContent}>
                        <span className={styles.optionLabel}>{reportType.label}</span>
                        {reportType.description && (
                          <span className={styles.optionDescription}>{reportType.description}</span>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              {hasReportTypeSelection ? '2. Select export type' : 'Select export type'}
            </h3>
          <div className={styles.options} role="radiogroup" aria-label="Export format">
            {formatOptions.map((option) => {
              const IconComponent = ICON_MAP[option.icon];
              const isSelected = selectedFormat === option.value;
              return (
                <label
                  key={option.value}
                  className={`${styles.optionCard} ${isSelected ? styles.optionCardSelected : ''}`}
                  onClick={() => setSelectedFormat(option.value)}
                >
                  <input
                    type="radio"
                    name="export-format"
                    value={option.value}
                    checked={isSelected}
                    onChange={() => setSelectedFormat(option.value)}
                    className={styles.radio}
                  />
                  {IconComponent && <IconComponent />}
                  <div className={styles.optionContent}>
                    <span className={styles.optionLabel}>{option.label}</span>
                    {option.description && (
                      <span className={styles.optionDescription}>{option.description}</span>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
          </div>
        </div>
        <div className={styles.modalFooter}>
          <Button type="button" variant="secondary" onClick={onClose}>
            {closeLabel}
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={confirmDisabled}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

ExportFormatDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  reportTypes: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
    })
  ),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
      icon: PropTypes.string,
      reportTypes: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  confirmLabel: PropTypes.string,
  closeLabel: PropTypes.string,
};

export default ExportFormatDialog;
